import type { ThreadStatus } from '../../requirement-lifecycle/types';

/**
 * Lifecycle status of the requirement thread, exactly as the backend reports
 * it. Aliased from the Lifecycle screen's type rather than restated, so the two
 * screens cannot disagree about what states exist.
 *
 * The Doctor used to keep its own four-value view
 * ('WIP' | 'Analyzing' | 'Analyzed' | 'Finalized') and collapse everything that
 * was not APPROVED into 'WIP'. That put DRAFT on screen as "WIP" — so a
 * force-approve was offered on a thread the approve API refuses — and it let an
 * analysis run overwrite an approval, because finishing analysis wrote
 * 'Analyzed' over 'Finalized'. Analysis progress is not a lifecycle state; it is
 * already visible in the quality-score column, which stays empty until a
 * requirement has been analysed.
 */
export type RequirementStatus = ThreadStatus;

/**
 * The taxonomy the backend's categorise analysis classifies into, in the
 * order the category filter offers them.
 */
export const REQUIREMENT_CATEGORIES = [
  'functional',
  'non_functional',
  'safety',
  'performance',
  'interface',
  'security',
  'reliability',
  'hmi',
  'diagnostics',
  'operational',
] as const;

/**
 * A taxonomy value, or "Unknown" when categorisation failed. Any other string
 * is still accepted: the value comes from an AI response, and a requirement
 * whose category falls outside the taxonomy must stay listable rather than
 * vanish from the filter.
 */
export type RequirementCategory =
  | (typeof REQUIREMENT_CATEGORIES)[number]
  | 'Unknown'
  | (string & {});
export type ViolationSeverity = 'High' | 'Medium' | 'Low';

export interface BrokenRule {
  rule: string;
  violation: string;
  fix: string;
  severity: ViolationSeverity;
}

export interface RequirementAnalysis {
  aspiceScore: number; // 0-10 (ASPICE compliance score)
  incoseScore: number; // 0-10 (INCOSE quality score)
  violations: number;
  category: RequirementCategory;
  categoryScore: number;
  brokenRules: BrokenRule[];
}

export interface Requirement {
  id: string;
  text: string;
  status: RequirementStatus;
  analysis?: RequirementAnalysis;
  // Set when this session's analysis of the current version failed, so the
  // review screen says so instead of reading as "no broken rules". `message`
  // is the backend's reason and may be absent.
  analysisFailure?: { message?: string };
  // Scores carried over from an analysis run in an EARLIER session. Kept
  // separate from `analysis`, which is only present once this session has
  // analysed the requirement and therefore also has the violations, category
  // and broken rules to back it up — synthesising those from a score alone
  // would put numbers on screen that nothing produced.
  aspiceScore?: number;
  incoseScore?: number;
  // Category from that same earlier analysis, so the list can be filtered by
  // it before this session has analysed anything.
  category?: RequirementCategory;
  createdAt: string;
  updatedAt: string;
  // Backend identifiers (from import/analyze). Used to call analyze/fix with the
  // real thread/version ids instead of positional placeholders.
  // int64 fields are received from the backend as JSON strings and kept as strings
  // throughout the frontend to avoid lossy number conversion.
  threadId?: string;
  versionId?: string;
  // A source of an open Merge proposal: it stays WIP but is
  // locked until the merge is accepted or rejected, so the list shows it as
  // pending merge.
  mergeLocked?: boolean;
}

/**
 * The status a requirement is shown, filtered and sorted under: a source locked
 * in an open Merge proposal is WIP on the backend but pending merge for the user.
 */
export function displayStatus(
  r: Pick<Requirement, 'status' | 'mergeLocked'>,
): RequirementStatus {
  return r.mergeLocked ? 'PENDING_MERGE' : r.status;
}

export interface FixAction {
  id: string;
  label: string;
  description: string;
  selected: boolean;
}

export interface RequirementFix {
  requirementId: string;
  before: string;
  after: string;
  actions: FixAction[];
  timestamp: string;
}

export interface ImportResult {
  total: number;
  imported: number;
  errors: string[];
}

export type AnalysisJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface AnalysisJob {
  id: string;
  requirementId: string;
  step: string;
  status: AnalysisJobStatus;
  progress: number; // 0-100
  error?: string;
  startedAt: string;
  completedAt?: string;
}

export interface AnalysisSession {
  id: string;
  totalRequirements: number;
  completedRequirements: number;
  jobs: AnalysisJob[];
  startedAt: string;
  cancelledAt?: string;
}
